/**
 * Translation Detection
 * Handles Google Translate detection and language management
 */

import { useSessionStore } from '@/stores'

export const TranslateTestString = 'Translate Test String - Do Not Remove'

export interface TranslationConfig {
  element: HTMLElement
  timeout?: number
  testString?: string
}

/**
 * Wait for translation to be ready
 */
export function waitForTranslation(config: TranslationConfig): Promise<boolean> {
  const { element, timeout = 1000, testString = TranslateTestString } = config

  return new Promise((resolve, reject) => {
    if (!element) {
      reject(new Error('Translation detector element not found'))
      return
    }

    // Set the test string
    element.textContent = testString

    let attempts = 0
    const maxAttempts = timeout / 50 // Check every 50ms

    const checkTranslation = () => {
      attempts++

      // Check if the content has changed (indicating translation occurred)
      const currentText = element.textContent || ''
      const hasTranslated = currentText !== testString

      if (hasTranslated) {
        resolve(true)
      } else if (attempts >= maxAttempts) {
        // Timeout reached, assume translation is not available or ready
        reject(new Error('Translation detection timeout'))
      } else {
        setTimeout(checkTranslation, 50)
      }
    }

    // Start checking after a brief delay
    setTimeout(checkTranslation, 100)
  })
}

/**
 * Get current language from Google Translate cookie
 */
export function getCurrentLanguageFromCookie(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  try {
    // Look for Google Translate cookie
    const cookies = document.cookie.split(';')
    const googTransCookie = cookies.find(cookie => cookie.trim().startsWith('googtrans='))

    if (googTransCookie) {
      const cookieValue = googTransCookie.split('=')[1]
      if (cookieValue && cookieValue.length > 0) {
        if (cookieValue.startsWith('/')) {
          const languages = cookieValue.slice(1).split('/')
          if (languages.length === 2) {
            return languages[1] // Return target language
          }
        }
      }
    }

    return null
  } catch (error) {
    return null
  }
}

/**
 * Set Google Translate cookie and reload
 */
export function setTranslationLanguage(language: string): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return
  }

  try {
    // Set the Google Translate cookie
    document.cookie = `googtrans=/en/${language}; path=/`

    // Set the hash for Google Translate
    window.location.hash = `googtrans(en|${language})`

    // Reload the page to apply translation
    window.location.reload()
  } catch (error) {}
}

/**
 * Create translation detector element
 */
export function createTranslationDetector(): HTMLElement {
  const element = document.createElement('div')
  element.style.display = 'none'
  element.textContent = TranslateTestString
  document.body.appendChild(element)
  return element
}

/**
 * Setup translation detection system
 */
export function setupTranslationDetection(
  onLanguageReady: (translated: boolean) => void,
  timeout: number = 1000
): { element: HTMLElement; cleanup: () => void } {
  if (typeof document === 'undefined') {
    return {
      element: null as any,
      cleanup: () => {},
    }
  }

  const detectorElement = createTranslationDetector()

  // Start translation detection
  const runDetection = async () => {
    try {
      const translated = await waitForTranslation({ element: detectorElement, timeout })
      onLanguageReady(translated)
    } catch {
      onLanguageReady(false)
    }
  }
  runDetection()

  const cleanup = () => {
    if (detectorElement && detectorElement.parentNode) {
      detectorElement.parentNode.removeChild(detectorElement)
    }
  }

  return { element: detectorElement, cleanup }
}

/**
 * Handle iframe translation message
 */
export function handleIframeTranslation(language: string): void {
  setTranslationLanguage(language)
}

/**
 * Get domain for translation cookies (without subdomain)
 */
export function getTranslationDomain(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const hostname = window.location.hostname
  const parts = hostname.split('.')

  // Return domain without subdomain for cookie setting
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.')
  }

  return hostname
}

/**
 * Get locale for translation from current language
 */
export function getTranslationLocale(): string {
  const currentLang = getCurrentLanguageFromCookie()

  if (!currentLang) {
    return 'en_US'
  }

  // Map language codes to locales
  const localeMap: Record<string, string> = {
    en: 'en_US',
    es: 'es_ES',
    fr: 'fr_FR',
    de: 'de_DE',
    it: 'it_IT',
    pt: 'pt_PT',
    zh: 'zh_CN',
    ja: 'ja_JP',
    ko: 'ko_KR',
    ru: 'ru_RU',
  }

  return localeMap[currentLang] || 'en_US'
}

/**
 * Delete cookie by name
 */
function deleteCookie(name: string, domain?: string): void {
  const cookieString = domain
    ? `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`
    : `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`

  document.cookie = cookieString
}

/**
 * Set cookie value
 */
function setCookie(name: string, value: string, domain?: string): void {
  const cookieString = domain ? `${name}=${value}; path=/; domain=${domain}` : `${name}=${value}; path=/`

  document.cookie = cookieString
}

/**
 * Select a new language and update translation
 */
export async function selectLanguage(code: string): Promise<void> {
  const sessionStore = useSessionStore.getState()

  const currentLanguage = getCurrentLanguageFromCookie()

  if (code === currentLanguage) {
    return
  }

  // Clear existing translation cookies
  deleteCookie('googtrans', window.location.hostname)
  deleteCookie('googtrans', getTranslationDomain())

  // Set new translation
  setCookie('googtrans', `/en/${code}`)

  // Update session store language
  sessionStore.setLanguage(code)

  // Update URL query params if needed
  const url = new URL(window.location.href)
  if (url.searchParams.has('language')) {
    url.searchParams.delete('language')
    window.history.replaceState({}, '', url.toString())
  }

  if (!sessionStore.isSessionActive()) {
    return
  }

  try {
    await sessionStore.updateSession({ language: code, locale: getTranslationLocale() })
  } catch (error: any) {
    console.error('Error updating session language:', error)
  } finally {
    // Reload the page to apply translation
    window.location.reload()
  }
}
